import { Button, IconButton, Tooltip, useAlert } from '@ads/components-react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useClassModuleForm } from '../../../context/ClassModuleFormContext';
import { ClassStudentAutocomplete } from '../../molecules/LookupAutocomplete';

const ROW_GRID = 'grid grid-cols-[1fr_3.5rem] items-center gap-micro';

/**
 * Seção "Turma": vincula turmas ao módulo via autocomplete + "Adicionar",
 * listando-as em tabela com remoção (confirmada) e empty state.
 */
export function LinkedClassesSection() {
  const { t } = useTranslation('classModuleManagement');
  const { alert } = useAlert();
  const { linkedClasses, addLinkedClass, requestRemoveLinkedClass } = useClassModuleForm();

  const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);
  // Remonta o autocomplete ao adicionar para limpar o texto exibido: zerar apenas
  // o `selected` não reseta o estado interno (`query`) do Autocomplete do ADS.
  const [resetKey, setResetKey] = useState(0);

  const handleAdd = () => {
    if (!selected) {
      alert({ alertType: 'warning', title: t('classes.selectFirst') });
      return;
    }
    const added = addLinkedClass({
      classStudentId: selected.id,
      name: selected.name,
      course: '',
    });
    if (!added) {
      alert({ alertType: 'warning', title: t('classes.alreadyLinked') });
      return;
    }
    setSelected(null);
    setResetKey(n => n + 1);
  };

  return (
    <div className="flex flex-col gap-mini">
      <div className="border-ctx-layout-border border-b-hairline pb-nano">
        <h2 className="text-ctx-content-title ts-heading-xs">{t('classes.title')}</h2>
      </div>

      <div className="flex items-end gap-micro">
        <div className="max-w-[35rem] flex-1">
          <ClassStudentAutocomplete
            key={resetKey}
            label={t('classes.linkLabel')}
            placeholder={t('classes.linkPlaceholder')}
            value={selected?.id ?? null}
            selectedLabel={selected?.name ?? ''}
            onChange={(id, label) => setSelected(id !== null ? { id, name: label } : null)}
          />
        </div>
        <Button hierarchy="secondary" size="md" onClick={handleAdd}>
          {t('classes.add')}
        </Button>
      </div>

      <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
        <div
          className={`${ROW_GRID} bg-ctx-layout-body px-mini py-micro text-ctx-content-base ts-heading-xxxs`}
        >
          <span>{t('classes.columns.class')}</span>
          <span />
        </div>

        {linkedClasses.length === 0 ? (
          <p className="px-mini py-xxs text-center text-ctx-content-placeholder ts-paragraph-xs">
            {t('classes.empty')}
          </p>
        ) : (
          linkedClasses.map((item, index) => (
            <div
              key={item.classStudentId}
              className={`${ROW_GRID} border-ctx-layout-border border-t-hairline px-mini py-micro text-ctx-content-title ts-paragraph-xxs`}
            >
              <Tooltip title={item.name} side="top" align="start">
                <span className="line-clamp-2">{item.name}</span>
              </Tooltip>
              <IconButton
                icon={TrashIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('classes.remove')}
                onClick={() => requestRemoveLinkedClass(index)}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
